import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import { SvgIcon, SvgIconProps } from 'components/helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import styles from './SocialBar.module.scss';

export interface SocialMediaPlatform {
  url: string;
  imageSrc: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
  title?: string;
}

export interface SocialBarData {
  headlineText: string;
  platforms: SocialMediaPlatform[];
  facebookUrl?: string;
  instagramUrl?: string;
  xUrl?: string;
  tiktokUrl?: string;
  pinterestUrl?: string;
  youtubeUrl?: string;
}

type Props = {
  socialBarData?: SocialBarData;
};

export default function SocialBar({ socialBarData }: Props) {
  // Translations
  const { getDictionaryValue } = useDictionary();

  // Return early if no data
  if (!socialBarData) {
    return null;
  }

  const hasPlatformItems = socialBarData.platforms.length > 0;

  // Legacy fallback: template URL fields rendered as SVG icons when no
  // Social Media Platform items have been authored.
  const legacySocialProperties = [
    {
      url: socialBarData.facebookUrl,
      iconName: 'facebook',
      iconFillColor: '#1877F2',
      message: `${getDictionaryValue('Navigation.SocialBar.FacebookFollow') || 'Like us on Facebook'}`,
    },
    {
      url: socialBarData.instagramUrl,
      iconName: 'instagram-gradient',
      message: `${getDictionaryValue('Navigation.SocialBar.InstagramFollow') || 'Follow us on Instagram'}`,
    },
    {
      url: socialBarData.xUrl,
      iconName: 'x',
      iconFillColor: '#0F1419',
      message: `${getDictionaryValue('Navigation.SocialBar.XFollow') || 'Follow us on X'}`,
    },
    {
      url: socialBarData.tiktokUrl,
      iconName: 'tiktok',
      iconFillColor: '#000B1F',
      message: `${getDictionaryValue('Navigation.SocialBar.TikTokFollow') || 'Follow us on TikTok'}`,
    },
    {
      url: socialBarData.pinterestUrl,
      iconName: 'pinterest',
      iconFillColor: '#BB171F',
      message: `${getDictionaryValue('Navigation.SocialBar.PinterestFollow') || 'Follow us on Pinterest'}`,
    },
    {
      url: socialBarData.youtubeUrl,
      iconName: 'youtube',
      iconFillColor: '#FF0000',
      message: `${getDictionaryValue('Navigation.SocialBar.YoutubeFollow') || 'Follow us on YouTube'}`,
    },
  ];

  return (
    <>
      <EditingHelpText>
        Add at least one Social Media Platform item to load this component.
      </EditingHelpText>
      <div className={`${styles.socialBar} printHide`}>
        <h2 id="gmi-social-bar-heading" className={styles.socialBarHeading}>
          {socialBarData.headlineText}
        </h2>
        <ul className={styles.socialBarList} aria-labelledby="gmi-social-bar-heading">
          {hasPlatformItems
            ? socialBarData.platforms.map(
                (platform, key) =>
                  platform.url &&
                  platform.imageSrc && (
                    <li className={styles.socialBarListItem} key={key}>
                      <a
                        className={styles.socialBarItem}
                        href={platform.url}
                        target="_blank"
                        rel={'noopener noreferrer'}
                        aria-label={platform.imageAlt || platform.title}
                      >
                        <EnhancedNextImage
                          className={styles.socialBarIcon}
                          src={platform.imageSrc}
                          alt={platform.imageAlt || ''}
                          width={platform.imageWidth || 32}
                          height={platform.imageHeight || 32}
                        />
                      </a>
                    </li>
                  )
              )
            : legacySocialProperties.map(
                (socialProperty, key) =>
                  socialProperty.url && (
                    <li className={styles.socialBarListItem} key={key}>
                      <a
                        className={styles.socialBarItem}
                        href={socialProperty.url}
                        target="_blank"
                        rel={'noopener noreferrer'}
                      >
                        <span style={{ color: socialProperty.iconFillColor }}>
                          <SvgIcon
                            className={styles.socialBarIcon}
                            icon={socialProperty.iconName as SvgIconProps['icon']}
                            size="md"
                          />
                          <span className="screenReaderText">{socialProperty.message}</span>
                        </span>
                      </a>
                    </li>
                  )
              )}
        </ul>
      </div>
    </>
  );
}
